import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import type { RiderProfile } from '@/api/types';
import { shiftLabel } from '@/lib/status';
import { cn } from '@/lib/utils';
import { ShiftSwitch } from './shift-switch';
import { Button } from './ui/button';

/**
 * The frame every signed-in screen sits in.
 *
 * One column at 30rem, centred, on every viewport. Not a max-width on a wide
 * page: this IS the app. A rider who opens it on a laptop at the office should
 * see the same single column rather than a run sheet stretched across a
 * monitor, because the layout they learned on a phone is the one they have to
 * use on a doorstep.
 *
 * Split into a frame and a body so the header can sit OUTSIDE the padded
 * column and still stick to the top of the viewport — a sticky element inside
 * a gutter would stop short of both edges, which reads as a rendering bug on
 * the one screen the rider looks at most.
 */
export function PageFrame({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-dvh flex-col bg-page">{children}</div>;
}

export function Main({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <main
      id="main"
      className={cn(
        'mx-auto w-full max-w-app flex-1 px-gutter',
        // Clear of the tab bar, and of the home bar under it.
        'pb-[calc(var(--layout-tabbar)+env(safe-area-inset-bottom)+var(--space-6))]',
        className,
      )}
    >
      {children}
    </main>
  );
}

const STICKY_BAR = [
  'sticky top-0 z-30 border-b border-line',
  'bg-page/90 backdrop-blur-xl supports-[not(backdrop-filter:blur(0))]:bg-page',
].join(' ');

/**
 * The bar over the run sheet: who you are, and whether you are working.
 *
 * Given the top of the main screen deliberately. Being on shift is what makes
 * dispatch send this phone deliveries, and a rider who has finished for the
 * evening but is still AVAILABLE keeps being handed jobs nobody will pick up —
 * the only sign of which is an order ageing on a board they cannot see. Buried
 * in a settings page it would be something they forget to turn off.
 */
export function ShiftHeader({ rider }: { rider: RiderProfile }) {
  const dot =
    rider.status === 'SUSPENDED'
      ? 'bg-danger'
      : rider.status === 'AVAILABLE' || rider.status === 'BUSY'
        ? 'bg-primary'
        : 'bg-muted';

  return (
    <header className={STICKY_BAR}>
      <div className="mx-auto flex h-header w-full max-w-app items-center gap-3 px-gutter">
        {/*
         * The dot is emphasis, never the message: the caption underneath says
         * the same thing in words, and the switch on the right says it a third
         * way by its position.
         */}
        <span
          className={cn('size-2.5 shrink-0 rounded-full forced-outline', dot)}
          aria-hidden="true"
        />

        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold leading-tight text-ink">{rider.name ?? 'Rider'}</p>
          <p className="truncate text-xs leading-tight text-ink-muted">{shiftLabel(rider)}</p>
        </div>

        <ShiftSwitch rider={rider} />
      </div>
    </header>
  );
}

/** The bar over everything that is not the run sheet. */
export function PageHeader({
  title,
  back = false,
  action,
}: {
  title: string;
  back?: boolean;
  action?: React.ReactNode;
}) {
  const navigate = useNavigate();

  return (
    <header className={STICKY_BAR}>
      <div className="mx-auto flex h-header w-full max-w-app items-center gap-1 px-2">
        {back ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              /*
               * `-1` rather than a hard link to /jobs: a delivery is reached
               * from the run sheet and from the history, and dropping someone
               * on the run sheet after they read back over yesterday loses
               * their place in a list they were scrolling.
               */
              void navigate(-1);
            }}
          >
            <ArrowLeft aria-hidden="true" />
            <span className="sr-only-text">Back</span>
          </Button>
        ) : (
          <span className="w-3" aria-hidden="true" />
        )}

        <h1 className="min-w-0 flex-1 truncate px-1 text-lg font-bold tracking-tight text-ink">
          {title}
        </h1>

        {action}
      </div>
    </header>
  );
}
